/* eslint-disable react/no-array-index-key */
import classNames from 'classnames'
import { ReactElement } from 'react'
import { Box, Icon } from '@alto-avios/alto-ui'
import { RatingsProps } from './Ratings.types'
import styles from './Ratings.module.scss'

const Ratings = ({
  rating,
  size,
  showEmptyStars,
  showRatingText,
  additionalText,
  isStarRating,
  className,
  compactText = false,
  compactSpacing = false,
}: RatingsProps): ReactElement<React.PropsWithChildren> => {
  const roundRatingToNearestHalf = Math.round(rating * 2) / 2
  const numFullStars = Math.floor(roundRatingToNearestHalf)
  const fullStars = new Array(numFullStars).fill(undefined)
  const isDecimalRating = Boolean(roundRatingToNearestHalf - numFullStars)
  const emptyStars = new Array(5 - numFullStars - (isDecimalRating ? 1 : 0)).fill(undefined)
  const starSize = size ?? 13
  const roundedRatingText = `${roundRatingToNearestHalf}/5`
  const spokenRatingText = isStarRating
    ? `${roundRatingToNearestHalf} stars`
    : `Rating: ${roundRatingToNearestHalf} out of 5`

  return (
    <div
      className={classNames(
        styles.ratingWrapper,
        compactText && styles.compactText,
        compactSpacing && styles.compactSpacing,
        className
      )}
      role="img"
      aria-label={spokenRatingText}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
    >
      {fullStars.map((_v, i) => (
        <Icon
          key={i}
          iconName="star"
          iconPrefix="fas"
          boxSize={`${starSize}px`}
          aria-hidden="true"
          className={styles.ratingStar}
          data-testid="rating-icon-filled-star"
        />
      ))}
      {isDecimalRating ? (
        <Icon
          iconName="star-half-alt"
          iconPrefix="far"
          boxSize={`${starSize}px`}
          aria-hidden="true"
          color="currentColor"
          className={styles.ratingStar}
          data-testid="rating-icon-half-star"
        />
      ) : null}
      {showEmptyStars
        ? emptyStars.map((_v, i) => {
            return (
              <Icon
                key={i}
                iconName="star"
                iconPrefix="far"
                boxSize={`${starSize}px`}
                aria-hidden="true"
                className={styles.ratingStar}
                data-testid="rating-icon-empty-star"
              />
            )
          })
        : null}
      <Box className={styles.ratingTextWrapper} style={{ mt: `${-starSize / 10}px` }}>
        {showRatingText && (
          <small
            className={styles.ratingText}
            style={compactText ? undefined : { fontSize: starSize }}
          >
            {roundedRatingText}
          </small>
        )}
        {additionalText && (
          <small
            className={styles.additionalText}
            style={compactText ? undefined : { fontSize: starSize }}
          >
            {additionalText}
          </small>
        )}
      </Box>
    </div>
  )
}

export default Ratings
